import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Eye, EyeOff, Save, User } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
`;

const Input = styled.input`
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 1rem;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 38px;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
`;

const InlineGroup = styled.div`
  position: relative;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.5rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: #0a0e17;
  font-weight: 700;
  cursor: pointer;
`;

export const AdminProfilePage: React.FC = () => {
  const { admin, refreshAdmin } = useAdminAuth();
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });

  const loadAdmin = async () => {
    try {
      const response = await api.adminMe();
      if (response.success && response.data) {
        setForm(prev => ({
          ...prev,
          name: response.data.name || '',
          email: response.data.email || '',
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar admin:', error);
    }
  };

  useEffect(() => {
    if (admin) {
      setForm(prev => ({
        ...prev,
        name: admin.name || '',
        email: admin.email || '',
      }));
    } else {
      loadAdmin();
    }
  }, [admin]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      if (form.new_password && form.new_password !== form.new_password_confirmation) {
        showError('As senhas nao coincidem.');
        setIsSaving(false);
        return;
      }

      if (form.new_password && !form.current_password) {
        showError('Informe a senha atual para alterar a senha.');
        setIsSaving(false);
        return;
      }

      const payload: any = {
        name: form.name,
        email: form.email,
      };

      if (form.new_password) {
        payload.current_password = form.current_password;
        payload.new_password = form.new_password;
        payload.new_password_confirmation = form.new_password_confirmation;
      }

      const response = await api.adminUpdateProfile(payload);
      if (response.success) {
        showSuccess('Dados atualizados com sucesso!');
        await refreshAdmin();
        setForm(prev => ({
          ...prev,
          current_password: '',
          new_password: '',
          new_password_confirmation: '',
        }));
      } else {
        showError(response.message || 'Erro ao atualizar perfil');
      }
    } catch (error: any) {
      const serverMessage = error.response?.data?.message;
      const fieldErrors = error.response?.data?.errors as Record<string, string[]> | undefined;
      const firstError = fieldErrors ? Object.values(fieldErrors)[0]?.[0] : null;
      showError(firstError || serverMessage || 'Erro ao atualizar perfil');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <Container>
        <Header>
          <Title>
            <User size={28} />
            Perfil do Administrador
          </Title>
        </Header>

        <Card>
          <Form onSubmit={handleSubmit}>
            <Field>
              <Label>Nome</Label>
              <Input
                type="text"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </Field>
            <Field>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                required
              />
            </Field>
            <Field>
              <Label>Senha atual</Label>
              <InlineGroup>
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={form.current_password}
                  onChange={(event) => setForm({ ...form, current_password: event.target.value })}
                />
                <PasswordToggle type="button" onClick={() => setShowCurrent(!showCurrent)}>
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </InlineGroup>
            </Field>
            <Field>
              <Label>Nova senha</Label>
              <InlineGroup>
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={form.new_password}
                  onChange={(event) => setForm({ ...form, new_password: event.target.value })}
                />
                <PasswordToggle type="button" onClick={() => setShowNew(!showNew)}>
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </InlineGroup>
            </Field>
            <Field>
              <Label>Confirmar nova senha</Label>
              <InlineGroup>
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  value={form.new_password_confirmation}
                  onChange={(event) => setForm({ ...form, new_password_confirmation: event.target.value })}
                />
                <PasswordToggle type="button" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </InlineGroup>
            </Field>
            <Button type="submit" disabled={isSaving}>
              <Save size={18} />
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Form>
        </Card>
      </Container>
    </AdminLayout>
  );
};
